import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { ThemeProvider } from 'styled-components';
import { Button, Alert, ConfigProvider, TabsProps } from 'antd';
import type { AppDispatch, RootState } from '../../redux/store';
import {
  fetchUserProfile,
  updateUserProfile,
  setupTwoFactor,
  confirmTwoFactor,
  resendTwoFactor,
  disableTwoFactor,
  clearTwoFactorSetup,
} from './store/settingsSlice';
import type { UpdateProfilePayload } from './store/settingsSlice';
import { Container, HeaderSection, Title, Subtitle, StyledTabs, GridContainer } from './Styles';
import { useTheme } from '../../context/ThemeContext';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import { notification } from '../../commonComponents/Notification/Notification';
import { parseLoyaltyRatio } from './constants';
import ProfileSection from './General/ProfileSection';
import PersonalInformationSection from './General/PersonalInformationSection';
import AddressSection from './Business/AddressSection';
import PreferencesSection from './Preferences/PreferencesSection';
import TwoStepVerificationSection from './Security/TwoStepVerificationSection';
import AccountDangerSection from './Account/AccountDangerSection';
import DomainSection from './Domain/DomainSection';
import AdminDomainSection from './Domain/AdminDomainSection';
import type { SettingsProps, TwoFactorMethod, FormData } from './types';

const Settings: React.FC<SettingsProps> = ({ role }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const theme = useMemo<{ mode: 'light' | 'dark' }>(
    () => ({ mode: isDarkMode ? 'dark' : 'light' }),
    [isDarkMode]
  );
  const { user, loading, error, twoFactorSetup } = useSelector((state: RootState) => state.settings);

  const { handleSubmit, reset, watch, setValue } = useForm<FormData>({
    defaultValues: {
      name: '',
      username: '',
      address: '',
      email: '',
      phone: '',
      publicPhone: '',
      loyaltyEnabled: false,
      loyaltyRedeemRatio: '',
      loyaltySpendRatio: '',
      receiveNotifications: false,
      autoRenewSubscription: false,
      twoFactorEnabled: false,
      twoFactorMethod: 'phone',
      twoFactorPhone: '',
      twoFactorEmail: '',
    },
  });

  const formData = watch();
  const [activeTab, setActiveTab] = useState(
    () => new URLSearchParams(window.location.search).get('tab') || 'general'
  );
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    dispatch(fetchUserProfile({ role }));
  }, [dispatch, role]);

  useEffect(() => {
    if (user) {
      reset({
        name: user.name || '',
        username: user.username || '',
        address: user.address || '',
        email: user.email || '',
        phone: user.phone || '',
        publicPhone: user.publicPhone || '',
        loyaltyEnabled: user.loyaltyEnabled || false,
        loyaltyRedeemRatio: user.loyaltyRedeemRatio || '',
        loyaltySpendRatio: user.loyaltySpendRatio || '',
        receiveNotifications: user.receiveNotifications || false,
        autoRenewSubscription: user.autoRenewSubscription || false,
        twoFactorEnabled: user.twoFactorEnabled || false,
        twoFactorMethod: (user.twoFactorMethod || 'phone') as TwoFactorMethod,
        twoFactorPhone: user.twoFactorPhone || '',
        twoFactorEmail: user.twoFactorEmail || '',
      });
    }
  }, [user, reset]);

  const handleEdit = useCallback((section: string) => {
    setEditingSection(section);
  }, []);

  const onSave = useCallback(
    async (data: FormData) => {
      if (!user) return;

      try {
        const loyaltyRedeemNum = parseLoyaltyRatio(data.loyaltyRedeemRatio);
        const loyaltySpendNum = parseLoyaltyRatio(data.loyaltySpendRatio);

        const updatedData: UpdateProfilePayload = {
          name: data.name.trim(),
          username: data.username.trim(),
          address: data.address.trim(),
          email: data.email.trim(),
          phone: data.phone.trim(),
          publicPhone: data.publicPhone?.trim() || undefined,
          loyaltyEnabled: data.loyaltyEnabled,
          receiveNotifications: data.receiveNotifications,
          autoRenewSubscription: data.autoRenewSubscription,
          ...(loyaltyRedeemNum !== undefined && { loyaltyRedeemRatio: loyaltyRedeemNum }),
          ...(loyaltySpendNum !== undefined && { loyaltySpendRatio: loyaltySpendNum }),
        };

        await dispatch(
          updateUserProfile({ role, userId: user.id, data: updatedData, logoFile: logoFile || undefined, removeLogo })
        ).unwrap();

        setLogoFile(null);
        setRemoveLogo(false);
        notification.success('Settings Updated', 'Your account settings have been updated successfully.');
        setEditingSection(null);
        setIsUploading(false);
      } catch (err: any) {
        notification.error(typeof err === 'string' ? err : err?.message || 'Failed to update settings.');
      }
    },
    [dispatch, role, user, logoFile, removeLogo]
  );

  const handleCancel = useCallback(() => {
    reset();
    setLogoFile(null);
    setRemoveLogo(false);
    setEditingSection(null);
  }, [reset]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchUserProfile({ role }));
  }, [dispatch, role]);

  const handleSetupTwoFactor = useCallback(async () => {
    if (!formData.twoFactorEnabled) {
      notification.error('Two-factor authentication is not enabled');
      return;
    }
    if (formData.twoFactorMethod === 'phone' && !formData.twoFactorPhone?.trim()) {
      notification.error('Please enter your personal phone number');
      return;
    }
    if (formData.twoFactorMethod === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!formData.twoFactorEmail?.trim() || !emailRegex.test(formData.twoFactorEmail.trim())) {
        notification.error('Please enter a valid personal email');
        return;
      }
    }
    try {
      const result = await dispatch(
        setupTwoFactor({
          method: formData.twoFactorMethod,
          twoFactorPhone: formData.twoFactorPhone?.trim(),
          twoFactorEmail: formData.twoFactorEmail?.trim(),
        })
      ).unwrap();
      notification.success('Verification Code Sent', result.message);
    } catch (err: any) {
      notification.error(typeof err === 'string' ? err : err?.message || 'Failed to setup 2FA');
    }
  }, [dispatch, formData.twoFactorEnabled, formData.twoFactorMethod, formData.twoFactorPhone, formData.twoFactorEmail]);

  const handleConfirmTwoFactor = useCallback(async () => {
    if (!twoFactorCode.trim()) { notification.error('Please enter the verification code'); return; }
    if (twoFactorCode.trim().length !== 6) { notification.error('Verification code must be 6 digits'); return; }
    if (!twoFactorSetup.normalizedContact) {
      notification.error('Normalized contact not found. Please setup 2FA again.');
      return;
    }
    try {
      await dispatch(
        confirmTwoFactor({ code: twoFactorCode.trim(), method: formData.twoFactorMethod, contact: twoFactorSetup.normalizedContact })
      ).unwrap();
      notification.success('2FA Enabled', 'Two-factor authentication has been successfully enabled.');
      setTwoFactorCode('');
      setEditingSection(null);
      dispatch(clearTwoFactorSetup());
    } catch (err: any) {
      notification.error(typeof err === 'string' ? err : err?.message || 'Failed to confirm 2FA');
    }
  }, [dispatch, twoFactorCode, formData.twoFactorMethod, twoFactorSetup.normalizedContact]);

  const handleResendTwoFactor = useCallback(async () => {
    if (!twoFactorSetup.normalizedContact) {
      notification.error('Contact not found. Please setup 2FA again.');
      return;
    }
    try {
      const result = await dispatch(
        resendTwoFactor({ method: formData.twoFactorMethod, contact: twoFactorSetup.normalizedContact })
      ).unwrap();
      notification.success('Code Resent', result.message);
      setTwoFactorCode('');
    } catch (err: any) {
      notification.error(typeof err === 'string' ? err : err?.message || 'Failed to resend code');
    }
  }, [dispatch, formData.twoFactorMethod, twoFactorSetup.normalizedContact]);

  const handleDisableTwoFactor = useCallback(async () => {
    try {
      await dispatch(disableTwoFactor()).unwrap();
      notification.success('2FA Disabled', 'Two-factor authentication has been successfully disabled.');
      setTwoFactorCode('');
      dispatch(clearTwoFactorSetup());
    } catch (err: any) {
      notification.error(typeof err === 'string' ? err : err?.message || 'Failed to disable 2FA');
    }
  }, [dispatch]);

  const handleDeactivateAccount = useCallback(async () => {
    notification.success('Account Deactivated', 'Your account has been temporarily deactivated. You can reactivate it anytime by logging in.');
  }, []);

  const handleDeleteAccount = useCallback(async () => {
    notification.success('Account Deleted', 'Your account has been permanently deleted.');
  }, []);

  const tabItems = useMemo<TabsProps['items']>(() => [
    {
      key: 'general',
      label: 'General',
      children: (
        <PersonalInformationSection
          user={user}
          editingSection={editingSection}
          loading={loading.update}
          profileLoading={loading.profile}
          onEdit={() => handleEdit('personal')}
          onSave={(data) => {
            setValue('name', data.name);
            setValue('email', data.email);
            setValue('phone', data.phone);
            handleSubmit(onSave)();
          }}
          onCancel={handleCancel}
        />
      ),
    },
    {
      key: 'business',
      label: 'Business',
      children: (
        <GridContainer>
          <AddressSection
            user={user}
            editingSection={editingSection}
            loading={loading.update}
            profileLoading={loading.profile}
            onEdit={() => handleEdit('address')}
            onSave={(data) => {
              setValue('address', data.address);
              setValue('publicPhone', data.publicPhone);
              handleSubmit(onSave)();
            }}
            onCancel={handleCancel}
          />
        </GridContainer>
      ),
    },
    {
      key: 'preferences',
      label: 'Preferences',
      children: (
        <PreferencesSection
          editingSection={editingSection}
          formData={{ receiveNotifications: formData.receiveNotifications, autoRenewSubscription: formData.autoRenewSubscription }}
          loading={loading.update}
          profileLoading={loading.profile}
          onEdit={() => handleEdit('additional')}
          onSwitchChange={(field, checked) => setValue(field as keyof FormData, checked)}
          onSave={handleSubmit(onSave)}
          onCancel={handleCancel}
        />
      ),
    },
    {
      key: 'security',
      label: 'Security',
      children: (
        <TwoStepVerificationSection
          editingSection={editingSection}
          formData={{
            twoFactorEnabled: formData.twoFactorEnabled,
            twoFactorMethod: formData.twoFactorMethod,
            twoFactorPhone: formData.twoFactorPhone,
            twoFactorEmail: formData.twoFactorEmail,
          }}
          profileLoading={loading.profile}
          setupLoading={loading.twoFactorSetup}
          twoFactorCode={twoFactorCode}
          twoFactorSetupInProgress={twoFactorSetup.inProgress}
          expiresAt={twoFactorSetup.expiresAt}
          onEdit={() => handleEdit('twoStep')}
          onSwitchChange={(field, checked) => setValue(field as keyof FormData, checked)}
          onInputChange={(e) => setValue(e.target.name as keyof FormData, e.target.value)}
          onMethodChange={(method) => setValue('twoFactorMethod', method)}
          onCancel={handleCancel}
          onSetupTwoFactor={handleSetupTwoFactor}
          onTwoFactorCodeChange={(code) => setTwoFactorCode(code)}
          onConfirmTwoFactor={handleConfirmTwoFactor}
          onResendTwoFactor={handleResendTwoFactor}
          onDisableTwoFactor={handleDisableTwoFactor}
        />
      ),
    },
    ...(role === 'admin'
      ? [{
          key: 'domain',
          label: 'Domain',
          children: <AdminDomainSection />,
        }]
      : role === 'merchant'
      ? [{
          key: 'domain',
          label: 'Domain',
          children: <DomainSection profileLoading={loading.profile} />,
        }]
      : []),
    {
      key: 'account',
      label: 'Account',
      children: (
        <AccountDangerSection
          loading={loading.update}
          onDeactivate={handleDeactivateAccount}
          onDelete={handleDeleteAccount}
        />
      ),
    },
  ], [
    role, user, editingSection, loading,
    formData.receiveNotifications, formData.autoRenewSubscription,
    formData.twoFactorEnabled, formData.twoFactorMethod, formData.twoFactorPhone, formData.twoFactorEmail,
    twoFactorCode, twoFactorSetup,
    handleEdit, handleSubmit, onSave, handleCancel, setValue,
    handleSetupTwoFactor, handleConfirmTwoFactor, handleResendTwoFactor,
    handleDisableTwoFactor, handleDeactivateAccount, handleDeleteAccount,
  ]);

  if (error && !user && !loading.profile) {
    return (
      <ThemeProvider theme={theme}>
        <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
          <Container>
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              action={<Button size="small" onClick={handleRefresh}>Try Again</Button>}
            />
          </Container>
        </ConfigProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderSection>
            <div>
              <Title level={1}>Account Settings</Title>
              <Subtitle>Manage your profile and preferences</Subtitle>
            </div>
          </HeaderSection>

          <ProfileSection
            user={user}
            role={role}
            loading={loading.update}
            profileLoading={loading.profile}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            onEdit={() => handleEdit('profile')}
            onLogoChange={(file) => { setLogoFile(file); setRemoveLogo(file === null); }}
            onSaveProfile={handleSubmit(onSave)}
            onCancelProfile={handleCancel}
          />

          <StyledTabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default React.memo(Settings);
